import { useState } from "react";
import { Check, ChevronDown, ChevronUp, X, Landmark } from "lucide-react";

const STAGE_LABEL = () => null;

function CustomerSummary({ j }) {
  const sm = j.summary || {};
  const rows = [
    ["Program", sm.program], ["Response submitted", sm.response_submitted ? `Yes${sm.response_submitted_at ? ` · ${sm.response_submitted_at.slice(0, 10)}` : ""}` : null],
    ["Award", sm.award_outcome && sm.award_outcome !== "PENDING" ? `${sm.award_outcome.replace("_", " ")}${sm.award_date ? ` · ${sm.award_date}` : ""}` : null],
    ["Purchase order", sm.purchase_order_placed ? "Placed with supplier" : null], ["Inspection", sm.inspection_status && sm.inspection_status !== "PENDING" ? sm.inspection_status.replace("_", " ") : null],
    ["Shipment", sm.carrier || sm.tracking_number ? [sm.carrier, sm.tracking_number, sm.ship_date].filter(Boolean).join(" · ") : null],
    ["Delivery", sm.delivery_status && sm.delivery_status !== "PENDING" ? `${sm.delivery_status}${sm.delivered_at ? ` · ${sm.delivered_at}` : ""}` : null],
    ["Completed", sm.completed_at ? sm.completed_at.slice(0, 10) : null],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <dl className="mt-3 grid sm:grid-cols-2 gap-x-5 gap-y-1 text-[12.5px]" data-testid="federal-customer-summary">
      {rows.map(([k, v]) => <div key={k} className="flex gap-2"><dt className="text-slate-500">{k}:</dt><dd className="font-semibold text-[var(--pg-navy)]">{v}</dd></div>)}
    </dl>
  );
}

export const FED_INFO = [
  ["Solicitation #", "solicitation_number"], ["Agency", "agency"], ["NSN", "nsn"], ["NIIN", "niin"], ["Part Number", "part_number"], ["SKU", "sku"],
  ["Manufacturer", "manufacturer"], ["CAGE", "cage_code"], ["Line Item", "line_item"], ["Quantity", "quantity"], ["Unit of Issue", "unit_of_issue"],
  ["Response Deadline", "response_deadline"], ["Required Delivery", "required_delivery_date"], ["Delivery Destination", "delivery_destination"], ["Set-Aside", "set_aside"],
];

export function FederalInfo({ rfq }) {
  const f = rfq.federal || {};
  const rows = FED_INFO.filter(([, k]) => f[k]);
  if (!rows.length) return null;
  return (
    <div className="mt-3 bg-[#f0f7ff] border border-[var(--pg-accent)]/30 rounded-xl p-4" data-testid={`portal-federal-info-${rfq.rfq_number}`}>
      <div className="flex items-center gap-2 text-[11px] font-[Poppins] font-bold uppercase tracking-wider text-[var(--pg-accent)]"><Landmark size={13} /> Federal Requirement Information</div>
      <dl className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-1.5 text-[13px]">
        {rows.map(([label, k]) => (
          <div key={k} className="flex gap-2"><dt className="text-slate-500 whitespace-nowrap">{label}:</dt><dd className="font-semibold text-[var(--pg-navy)] break-all">{f[k]}</dd></div>
        ))}
      </dl>
    </div>
  );
}

export function FederalJourney({ rfq }) {
  const [expanded, setExpanded] = useState(false);
  const j = rfq.federal_journey;
  if (!j) return null;
  const stages = j.stages || [];
  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`federal-tracker-${rfq.rfq_number}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-[Poppins] font-bold uppercase tracking-wider text-slate-500">{j.summary?.program || "Federal"} Requirement Tracker</span>
        <span data-testid={`federal-tracker-status-${rfq.rfq_number}`} className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${j.closed ? "bg-slate-200 text-slate-600" : "bg-[var(--pg-navy)] text-white"}`}>
          {j.closed ? "Not Awarded" : `Step ${j.current + 1} of ${stages.length}: ${j.label || stages[j.current]?.label}`}
        </span>
      </div>
      <p className="mt-2 text-[13px] text-[var(--pg-ink-soft)]" data-testid={`federal-tracker-message-${rfq.rfq_number}`}>{j.message}</p>
      <CustomerSummary j={j} />
      <div className="mt-3 flex items-start overflow-x-auto pb-1" data-testid={`federal-progress-${rfq.rfq_number}`}>
        {stages.map((s, i) => {
          const done = i < j.current || (j.closed && i < j.current);
          const active = i === j.current;
          const closedHere = j.closed && active;
          return (
            <div key={s.key} className="flex-1 min-w-[58px] flex flex-col items-center relative">
              {i > 0 && <div className={`absolute top-[11px] right-1/2 w-full h-[2.5px] ${i <= j.current ? "bg-[var(--pg-accent)]" : "bg-slate-200"}`} style={{ zIndex: 0 }} />}
              <div className={`relative z-10 w-[23px] h-[23px] rounded-full flex items-center justify-center text-[10px] font-bold ${closedHere ? "bg-slate-500 text-white" : done ? "bg-[var(--pg-accent)] text-white" : active ? "bg-[var(--pg-navy)] text-white ring-4 ring-[var(--pg-navy)]/15" : "bg-slate-200 text-slate-400"}`}>
                {closedHere ? <X size={12} /> : done ? <Check size={12} /> : i + 1}
              </div>
              <span className={`mt-1.5 text-[9.5px] leading-tight text-center font-[Poppins] font-semibold uppercase tracking-wide ${done || active ? "text-[var(--pg-navy)]" : "text-slate-400"}`}>{STAGE_LABEL(s.key) || s.label}</span>
            </div>
          );
        })}
      </div>
      <button data-testid={`federal-timeline-toggle-${rfq.rfq_number}`} onClick={() => setExpanded(!expanded)}
        className="mt-2 inline-flex items-center gap-1 text-[12px] font-[Poppins] font-semibold text-slate-500 hover:text-[var(--pg-navy)] transition-colors">
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {expanded ? "Hide timeline" : "View full timeline"}
      </button>
      {expanded && (
        <div className="mt-3" data-testid={`federal-timeline-${rfq.rfq_number}`}>
          {stages.map((s, i) => {
            const done = i < j.current;
            const active = i === j.current;
            const reached = done || active;
            return (
              <div key={s.key} className="flex gap-3 relative pb-4 last:pb-0">
                {i < stages.length - 1 && <div className={`absolute left-[10px] top-[22px] bottom-0 w-[2px] ${done ? "bg-[var(--pg-accent)]" : "bg-slate-200"}`} />}
                <div className={`relative z-10 w-[21px] h-[21px] rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${done ? "bg-[var(--pg-accent)] text-white" : active ? "bg-[var(--pg-navy)] text-white" : "bg-slate-100 text-slate-400"}`}>
                  {done ? <Check size={11} /> : i + 1}
                </div>
                <div>
                  <div className={`text-[13px] font-[Poppins] font-semibold ${reached ? "text-[var(--pg-navy)]" : "text-slate-400"}`}>
                    {STAGE_LABEL(s.key) || s.label}
                    {active && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-[var(--pg-navy)] text-white uppercase tracking-wide">{j.closed ? "Not Awarded" : "Current"}</span>}
                    {s.at && <span className="ml-2 text-[11px] font-normal text-slate-400">{s.at.slice(0, 10)}</span>}
                  </div>
                  
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
